// Libraries
import React from 'react';

// Supermove
import {Loading, Icon} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {UserModel, ProjectTypeModel, JobTypeModel} from '@supermove/models';

// App
import LargeModal from '@shared/design/components/Modal/LargeModal';
import Table from '@shared/design/components/Table';
import PageLoadingIndicator from '@shared/modules/App/components/PageLoadingIndicator';

const LeadProviderEndpointProjectTypesModal = ({
  isOpen,
  handleClose,
}: {
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const {loading, data} = useQuery<Response>(LeadProviderEndpointProjectTypesModal.query, {
    fetchPolicy: 'cache-and-network',
  });

  return (
    <LargeModal
      title={`Project & Job Types`}
      isOpen={isOpen}
      handlePrimaryAction={handleClose}
      primaryActionText={'Close'}
      primaryActionIcon={Icon.Xmark}
      handlePressOutside={handleClose}
      isScrollable
      isResponsive
    >
      <Loading loading={loading} as={PageLoadingIndicator}>
        {() => (
          <Content
            projectTypes={data!.viewer.viewingOrganization.projectTypes}
            isOpen={isOpen}
            handleClose={handleClose}
          />
        )}
      </Loading>
    </LargeModal>
  );
};

const Content = ({
  projectTypes,
  isOpen,
  handleClose,
}: {
  projectTypes: ProjectTypeModel[];
  isOpen: boolean;
  handleClose: () => void;
}) => {
  const items: Item[] = projectTypes.flatMap((projectType) =>
    projectType.jobTypes.map((jobType) => ({
      id: jobType.id,
      projectType,
      jobType,
    })),
  );
  return (
    <Table
      items={items}
      itemKey={'id'}
      columnDefinitions={[
        {
          headerLabel: 'Project Type',
          flex: 1,
          cellText: ({projectType}: Item) => projectType.name,
        },
        {
          headerLabel: 'Job Type',
          flex: 1,
          cellText: ({jobType}: Item) => jobType.name,
        },
        {
          headerLabel: 'Project Type Identifier',
          flex: 1,
          cellText: ({projectType}: Item) => projectType.slug,
        },
        {
          headerLabel: 'Job Type Identifier',
          flex: 1,
          cellText: ({jobType}: Item) => jobType.slug,
        },
      ]}
      hasBorder
      emptyStateText={'No project types found.'}
    />
  );
};

LeadProviderEndpointProjectTypesModal.query = gql`
    query OrganizationSettingsLeadProviderEndpointProjectTypesModal {
        ${gql.query}
        viewer {
            id
            viewingOrganization {
                id
                projectTypes {
                    id
                    name
                    slug
                    jobTypes {
                        id
                        name
                        slug
                    }
                }
            }
        }
    }
`;

interface Response {
  viewer: UserModel;
}

interface Item {
  id: string;
  projectType: ProjectTypeModel;
  jobType: JobTypeModel;
}

export default LeadProviderEndpointProjectTypesModal;
